import { deleteStudioVideo } from '@/api/studio-video';
import { useCurrentApp } from '@/stores/userDataStore';
import { Modal, TitleBar } from '@shopify/app-bridge-react';
import { Box, Text } from '@shopify/polaris';
import { useMutation } from '@tanstack/react-query';
import { type StudioVideo } from '@/features/ai-video-creator/stores/studioVideoStore';
import { useStudioVideoCache } from '../studio-videos/hooks/useStudioVideos';
import { useAllAiCreationsCache } from '../my-creations-library/hooks/useAllAiCreations';
import { useToast } from '@/hooks/useToast';
import { useState } from 'react';
import BasicBanner from '@/components/BasicBanner';

type DeleteStudioVideoModalProps = {
  video: StudioVideo;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

export const DeleteStudioVideoModal = ({ video, isOpen, setIsOpen }: DeleteStudioVideoModalProps) => {
  const { appKey } = useCurrentApp();
  const [error, setError] = useState<string>('');
  const { invalidateStudioVideosListQuery } = useStudioVideoCache();
  const { notify, notifyError } = useToast();
  const { invalidateAllAiCreationsQuery } = useAllAiCreationsCache({ appKey });
  const deleteMutation = useMutation({
    mutationFn: () => deleteStudioVideo({ appKey, id: video.id }),
    onSuccess: () => {
      invalidateStudioVideosListQuery();
      invalidateAllAiCreationsQuery();
      setIsOpen(false);
      notify('Video deleted');
    },
    onError: () => {
      setError('Failed to delete video');
    },
  });

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleDelete = () => {
    if (deleteMutation.isPending) return;
    deleteMutation.mutate();
  };

  return (
    <Modal open={isOpen} onHide={handleClose} variant="small">
      <TitleBar title="Delete Creation">
        <button variant="primary" onClick={handleDelete} disabled={deleteMutation.isPending}>
          Delete
        </button>
        <button onClick={handleClose} disabled={deleteMutation.isPending}>
          Cancel
        </button>
      </TitleBar>
      <Box padding="400">
        <BasicBanner
          tone="critical"
          title="Error"
          hidden={!error}
          boxComponentProps={{ paddingBlockEnd: '200' }}
          onDismiss={() => setError('')}
        >
          {error}
        </BasicBanner>
        <Text as="p" variant="headingSm">
          Are you sure you want to delete this creation?
        </Text>
      </Box>
    </Modal>
  );
};
